/**
 * MyPortal API Service Layer
 * Abstraktions-Layer für die MyPortal API mit Caching, Error-Handling und Request-Deduplication
 */

class MyPortalService {
  constructor(myportalClient) {
    this.client = myportalClient
    this.cache = new Map()
    this.pendingRequests = new Map()
    this.retryAttempts = new Map()
    this.maxRetries = 3
    this.retryDelay = 1000 // Start with 1 second
  }

  /**
   * Generiert einen Cache-Key für Requests
   */
  _getCacheKey(method, table, options = {}) {
    const key = `${method}:${table}:${JSON.stringify(options)}`
    return key
  }

  /**
   * Exponential Backoff für Retry-Logic
   */
  _getRetryDelay(attempt) {
    return this.retryDelay * Math.pow(2, attempt)
  }

  /**
   * Normalisiert API-Responses
   */
  _normalizeResponse(result) {
    if (!result) return { data: [], success: false }
    
    return {
      data: Array.isArray(result) ? result : (result.data || []),
      pagination: result.pagination || null,
      success: result.success !== false,
      total: result.pagination?.total || (Array.isArray(result) ? result.length : (result.data?.length || 0))
    }
  }

  /**
   * Request-Deduplication - verhindert doppelte API-Calls
   */
  async _executeWithDeduplication(cacheKey, requestFn) {
    // Prüfe ob Request bereits läuft
    if (this.pendingRequests.has(cacheKey)) {
      return await this.pendingRequests.get(cacheKey)
    }

    // Prüfe Cache
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey)
      const now = Date.now()
      
      // Cache ist noch gültig (5 Minuten)
      if (now - cached.timestamp < 300000) {
        return cached.data
      }
    }

    // Führe Request aus
    const promise = this._executeWithRetry(requestFn, cacheKey)
    this.pendingRequests.set(cacheKey, promise)

    try {
      const result = await promise
      
      // Cache das Ergebnis
      this.cache.set(cacheKey, {
        data: result,
        timestamp: Date.now()
      })
      
      return result
    } finally {
      this.pendingRequests.delete(cacheKey)
    }
  }

  /**
   * Führt Request mit Retry-Logic aus
   */
  async _executeWithRetry(requestFn, cacheKey) {
    let lastError
    const maxAttempts = this.maxRetries + 1

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const result = await requestFn()
        this.retryAttempts.delete(cacheKey)
        return this._normalizeResponse(result)
      } catch (error) {
        lastError = error
        
        // Keine Retry bei Validierungs-Fehlern
        if (error.status === 400 || error.status === 404) {
          break
        }

        // Letzter Versuch
        if (attempt === maxAttempts - 1) {
          break
        }

        // Warte vor nächstem Versuch
        const delay = this._getRetryDelay(attempt)
        await new Promise(resolve => setTimeout(resolve, delay))
        
        console.warn(`API retry attempt ${attempt + 1} for ${cacheKey}:`, error.message)
      }
    }

    throw lastError
  }

  /**
   * Lädt eine Collection von Items
   */
  async getCollection(table, options = {}) {
    const { forceRefresh = false, ...apiOptions } = options
    const cacheKey = this._getCacheKey('getCollection', table, apiOptions)

    if (forceRefresh) {
      this.cache.delete(cacheKey)
    }
    
    return await this._executeWithDeduplication(cacheKey, async () => {
      return await this.client.data.getItems({
        table,
        ...apiOptions
      })
    })
  }

  /**
   * Lädt ein Singleton-Item
   */
  async getSingleton(table, itemId) {
    const options = {
      filter: JSON.stringify({ itemId: `${itemId}-singleton` }),
      limit: 1,
      page: 1
    }
    
    const result = await this.getCollection(table, options)
    return {
      ...result,
      data: result.data.length > 0 ? result.data[0] : {}
    }
  }

  /**
   * Lädt Items mit Relations
   */
  async getWithRelations(table, relations, options = {}) {
    const cacheKey = this._getCacheKey('getWithRelations', table, { relations, ...options })
    
    return await this._executeWithDeduplication(cacheKey, async () => {
      const {
        page = 1,
        limit = 20,
        filter = null,
        sort = { createdAt: -1 }
      } = options

      return await this.client.data.getItemsWithRelations(
        table,
        page,
        limit,
        filter,
        sort,
        relations
      )
    })
  }

  /**
   * Erstellt ein neues Item
   */
  async createItem(table, data) {
    try {
      const result = await this.client.data.insertItems({ table, data })
      this._invalidateCache(table)
      return this._normalizeInsertResponse(result)
    } catch (error) {
      console.error(`Failed to create item in ${table}:`, error)
      throw error
    }
  }

  /**
   * Aktualisiert ein bestehendes Item
   */
  async updateItem(table, id, data) {
    try {
      const result = await this.client.data.updateItems({
        table,
        id,
        update: data
      })
      this._invalidateCache(table)
      return result
    } catch (error) {
      console.error(`Failed to update item in ${table}:`, error)
      throw error
    }
  }

  _normalizeInsertResponse(result) {
    const payload = result?.data ?? result
    const firstInsertedId = Array.isArray(payload?.insertedIds)
      ? payload.insertedIds[0]
      : null
    const insertedId = payload?.insertedId || payload?._id || firstInsertedId || null
    const id = insertedId?.toString ? insertedId.toString() : insertedId

    return {
      data: id ? { _id: id, insertedId: id } : payload,
      success: result?.success !== false
    }
  }

  /**
   * Invalidiert gecachte GET-Requests für eine Tabelle
   */
  invalidateTableCache(table) {
    this._invalidateCache(table)
  }

  /**
   * Invalidiert Cache für eine Tabelle
   */
  _invalidateCache(table) {
    const keysToDelete = []
    
    for (const key of this.cache.keys()) {
      if (key.includes(`:${table}:`)) {
        keysToDelete.push(key)
      }
    }
    
    keysToDelete.forEach(key => this.cache.delete(key))
  }

  /**
   * Leert den kompletten Cache
   */
  clearCache() {
    this.cache.clear()
    this.pendingRequests.clear()
    this.retryAttempts.clear()
  }

  /**
   * Cache-Statistiken für Debugging
   */
  getCacheStats() {
    return {
      cacheSize: this.cache.size,
      pendingRequests: this.pendingRequests.size,
      retryAttempts: this.retryAttempts.size
    }
  }
}

export default MyPortalService